import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faVolumeMute, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import { generateClient } from 'aws-amplify/api';
import { onSlideChange } from '../../../graphql/subscriptions';
import './producer-ppt.css';
import loadingPlaceholder from '../../../assets/loading.png';
import { useParams } from 'react-router';
import CpeCredit from '../../cpe-credit/cpe-credit';
import '../../../components/cpe-credit/cpe-credit.css';
import Hls from 'hls.js';
import PollCountPopUP from './poll-result';
import { useSlide } from "./SlideProvider";
const client = generateClient();

interface Slide {
    sequence: number;
    url?: string;
    type?: string;
    pollId?: string; // Added pollId for handling poll questions
    pollResults?: PollResult[]; // Added pollResults for handling poll results
    collateralTypeId?: string | null;
    id?: string;
}

interface PollQuestion {
    pollId: string;
    questionText: string;
    options: Record<string, string>; // Option keys and values
}

interface PollResult {
    option: string;
    percentage: number;
    voteCount: number;
}

const PptViewer: React.FC<any> = ({ slidesData, pollData, allPollResultData, slideControlEnabled, selectedSlideSequence, sdkData, customSlidesData, selectedSlideId}) => {
    const [isFullScreen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState<Slide | null>(null);
    const slideContainerRef = useRef<HTMLDivElement>(null);
    const { eventId } = useParams<{ eventId: string }>();
    const videoRef = useRef<HTMLVideoElement>(null); // Ref for HLS player
    const [isMuted, setIsMuted] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false); // New state for the popup
    // Mapping Poll Results
    const pollResultsMap: Record<string, PollResult[]> = {};
    pollData.forEach((poll: any, index: number) => {
        pollResultsMap[poll.pollId] = allPollResultData[index]?.results || [];
    });
    const { handleNextSlide, handlePreviousSlide, currentSlideSequence, slides} = useSlide();

    useEffect(() => {
        if (slidesData && slidesData.length > 0) {
            if (slides.length > slidesData.length) {
                const initialSlide = slides.find((slide: Slide) => (slide.collateralTypeId ? slide.collateralTypeId: slide?.id) === selectedSlideId && slide.type !== 'pollresult')
                setCurrentSlide(initialSlide  || null);
            } else {
                const initialSlide = slidesData.find((slide: Slide) => (slide.collateralTypeId ? slide.collateralTypeId: slide?.id) === selectedSlideId  && slide.type !== 'pollresult')
                setCurrentSlide(initialSlide);
            }
        }
    }, [slidesData]);
    

    useEffect(() => {
        const subscription = client.graphql({ query: onSlideChange }).subscribe({
            next: ({ data }) => {
                const newSlide = data.onSlideChange;
                if (newSlide && newSlide.id === eventId) {
                    const formattedSlide: Slide = {
                        sequence: newSlide.sequence,
                        url: newSlide.url,
                        type: newSlide.type,
                        pollId: newSlide.pollId, // Capture pollId if available
                        pollResults: newSlide.pollResults?.filter((result: any) => result !== null).map((result: any) => ({
                            option: result.option,
                            percentage: result.percentage,
                            voteCount: result.voteCount
                        })),
                        collateralTypeId:newSlide.collateralTypeId,
                    };
                    setCurrentSlide(formattedSlide);
                }
            },
            error: (error) => {
                console.error('Error with slide change subscription:', error);
            },
        });

        return () => subscription.unsubscribe();
    }, [eventId]);

    useEffect(() => {
        // Automatically open the modal for poll questions
        if (currentSlide?.type === 'pollQuestions' && currentSlide.pollId) {
            setIsModalOpen(true);
        } else {
            setIsModalOpen(false);
        }
    }, [currentSlide]);

    const toggleFullScreen = () => {
        const eleAssetWrapper = document.querySelector('.asset-wrapper') as HTMLElement;
        const elem = currentSlide?.type === 'video' || currentSlide?.type === 'HLSStream'
            ? slideContainerRef.current?.querySelector('video')
            : slideContainerRef.current;

        const enterFullScreen = () => {
            if (elem && elem.requestFullscreen) {
                eleAssetWrapper?.classList?.remove('live-view-panel');
                eleAssetWrapper?.classList?.add('live-view-panel1');
                elem.requestFullscreen().then(() => {
                    // Hide video controls when entering fullscreen
                    if (currentSlide?.type === 'video' || currentSlide?.type === 'HLSStream') {
                        if (elem instanceof HTMLVideoElement) {
                            elem.controls = false;
                            elem.style.pointerEvents = 'none'; // Disable pointer events to prevent control interaction
                        }
                    }
                }).catch(err => {
                    console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
                });
            }
        };

        const exitFullScreen = () => {
            eleAssetWrapper?.classList?.remove('live-view-panel1');
            eleAssetWrapper?.classList?.add('live-view-panel');
            if (document.exitFullscreen) {
                document.exitFullscreen().then(() => {
                    // Hide video controls when exiting fullscreen
                    if (currentSlide?.type === 'video' || currentSlide?.type === 'HLSStream') {
                        if (elem instanceof HTMLVideoElement) {
                            elem.controls = false;
                            elem.style.pointerEvents = 'none';  // Disable pointer events to prevent control interaction
                        }
                    }
                }).catch(err => {
                    console.log(`Error attempting to exit full-screen mode: ${err.message} (${err.name})`);
                });
            }
        };

        if (!document.fullscreenElement) {
            enterFullScreen();
        } else {
            exitFullScreen();
        }
    };

    // Capture escape button and fullscreen change event
    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                const eleAssetWrapper = document.querySelector('.asset-wrapper') as HTMLElement;
                eleAssetWrapper?.classList?.remove('live-view-panel1');
                eleAssetWrapper?.classList?.add('live-view-panel');
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const renderPollQuestion = (pollId: string) => {
        const poll = pollData.find((p: PollQuestion) => p.pollId === pollId);
        const pollType = poll?.pollType || 'radio'; // Default to 'radio' if pollType is not defined
        if (!poll) return null;

        return (
            <div className='right-side-ppt'>
            <div className="poll-question pollQ">
                <h3 className='right-side-question-wrapper question-text'>Q.{" "+poll.questionTitle}</h3>
                <form className='right-side-ppt-option'>
                    {Object.entries(poll.options).map(([key, value]) => (
                        <div key={key} style={{display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
                            <input
                                type={pollType === 'checkbox' ? 'checkbox' : 'radio'} // Dynamic input type based on pollType
                                id={key}
                                name={`question-${poll.pollId}`}
                                value={key}
                                style={{
                                    marginTop: '5px', // Slight adjustment to align with the text
                                    cursor: 'pointer',
                                    lineHeight: '1.5',
                                  }}
                            />
                            <label className='option-text-wrapper' htmlFor={key}>{String(value)}</label>
                        </div>
                    ))}
                </form>
            </div>
            </div>
        );
    };

    const PollResults = React.memo(({ pollResults }: { pollResults: PollResult[] }) => {
        return (
            <div className="poll-result right-side-ppt-live-view">
                <h3 className='right-side-question-wrapper'>Results</h3>
                <div className='live-view-result-show'>
                    {pollResults
                        .sort((a, b) => b.percentage - a.percentage)
                        .map((result) => (
                            <div key={result.option} className="poll-result-item">
                                <div className="poll-option">{result.option}</div>
                                <div className="poll-percentage">
                                    <div className="poll-bar" style={{ width: `${result.percentage}%`, backgroundColor: '#007bff' }}></div>
                                    <div className="poll-text">{result.percentage}%</div>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        );
    });

    const renderCustomSlide = (collateralTypeId: string) => {
        const customSlide = customSlidesData.find((cs: any) => cs.collateralTypeId === collateralTypeId);
        if (!customSlide) return null;
        return (
            <div className="custom-slide-wrapper-live-view">
                <h2 className="live-slide-title-live-view">{customSlide.title}</h2>
                <p className='live-slide-description-live-view'>{customSlide.description}</p>
                {/* Render multiple hyperlinks */}
                {customSlide.hyperlinks && customSlide.hyperlinks.length > 0 && (
                    <div className="custom-slide-links-live-view">
                        {customSlide.hyperlinks.map((link: { title: string; url: string }, index: number) => (
                            <div key={index} className="custom-slide-link-view">
                                <a href={link.url} target="_blank" rel="noopener noreferrer" title={link.url}>
                                    {link.title}
                                </a>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    useEffect(() => {
        if (currentSlide?.type === 'HLSStream' && currentSlide.url && Hls.isSupported()) {
          const hls = new Hls();
          hls.loadSource(currentSlide.url);
          hls.attachMedia(videoRef.current!);
        } else if (currentSlide?.type === 'video/mp4') {
            if (videoRef.current) {
                videoRef.current.pause(); // Stop playback
                videoRef.current.removeAttribute('src'); // Remove the src attribute
                videoRef.current.load(); // Reset the video
            }
        }
      }, [currentSlide]);

    // Separate useEffect to handle mute state changes without reloading video
    useEffect(() => {
        if (videoRef.current && (currentSlide?.type === 'HLSStream' || currentSlide?.type === 'video' || currentSlide?.type === 'video/mp4')) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted, currentSlide?.type]);

    return (
            <div className="main-ppt-wrapper">
            <div className="presenter-live-view-nav-bar">
                <div className="main-ppt-wrapper-live-view">
                    <span className="red-dot"></span>
                    <h6>Live view</h6>
                    {/* <button className="arrow-button left-arrow-live-view" onClick={handlePreviousSlide}
                    disabled={currentSlideSequence === 1 || (sdkData.role === 0 && !slideControlEnabled)}
                    style={{
                        cursor: sdkData.role === 0 && !slideControlEnabled ? "not-allowed" : "pointer",
                    }}>
                        <img  className="left-arrow" src={Back} alt="back-arrow" />
                    </button>
                    <button className="arrow-button right-arrow-live-view" onClick={handleNextSlide}
                    disabled={(sdkData.role === 0 && !slideControlEnabled)}
                    style={{
                        cursor: sdkData.role === 0 && !slideControlEnabled ? "not-allowed" : "pointer",
                    }}>
                        <img  className="right-arrow" src={Next} alt="back-arrow" />
                    </button> */}
                </div>
            </div>

            <div ref={slideContainerRef} className="position-relative slide-container" style={{ width: '100%' }}>
                <button onClick={toggleFullScreen} className="btn btn-light position-absolute end-0 m-1 full-speaker-btn">
                    <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} style={{ fontSize: '1.2rem' }} />
                </button>
                <div className='asset-wrapper live-view-panel'>
                    {currentSlide?.type === 'HLSStream' ? (
                    <>
                        <video
                            autoPlay
                            ref={videoRef}
                            style={{ width: '100%', objectFit: 'contain',  borderBottomRightRadius: '9px', borderBottomLeftRadius: '9px'}}
                        />
                        <div style={{ position: 'absolute', bottom: '10px', left: '15px', display: 'flex', gap: '10px' }}>
                            <button onClick={toggleMute} className="btn btn-light">
                                <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
                            </button>
                        </div>
                    </>
                    ): currentSlide?.type === 'video' || currentSlide?.type === 'video/mp4' ? (
                        <video ref={videoRef} autoPlay style={{ width: '100%', objectFit: 'contain', pointerEvents: 'none',   borderBottomRightRadius: '9px', borderBottomLeftRadius: '9px' }}>
                            <source src={currentSlide.url} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    ) : currentSlide?.type === 'pollQuestions' && currentSlide.pollId ? (
                        <>
                            <PollCountPopUP
                                isModalOpen={isModalOpen}
                                toggleModal={() => setIsModalOpen(false)}
                                currentSlide={{ pollId: currentSlide.pollId }}
                            />
                            {renderPollQuestion(currentSlide.pollId)}
                        </>
                    ) : currentSlide?.type === 'pollresult' && currentSlide.pollId ? (
                        <PollResults pollResults={currentSlide.pollResults || []} />
                    
                    ) : currentSlide?.type === 'cpeForm' ? (
                       <CpeCredit className="file2-classname" />
                    ) : currentSlide?.type === 'customSlide' && currentSlide.collateralTypeId ? (
                        <>
                        {renderCustomSlide(currentSlide.collateralTypeId)}
                        </>
                    ) : (
                        <img crossOrigin="anonymous" src={currentSlide?.url || loadingPlaceholder} alt={`Slide ${currentSlide?.sequence || 'default'}`} style={{ width: '100%', height: '100%', objectFit: 'contain'}} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PptViewer;
